// src/components/mockExam/RedPenCards.jsx
//
// Row 3 of the exam landing page: grammar, vocabulary and Redemittel as three
// paper index cards that a teacher's red pen corrects. The cards drop in and the
// pen draws once, the first time the row scrolls into view; there is no loop.
// Content comes from getRedPen() in src/data/examShowcase.js: the level's grammar
// and vocabulary cards plus the exam's own Redemittel phrase and count.
//
// The handwriting is Caveat, loaded here rather than globally because this row
// is the only place that uses it.
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.025em' };
const HAND = { fontFamily: "'Caveat', cursive" };
const CAVEAT_HREF = 'https://fonts.googleapis.com/css2?family=Caveat:wght@600&display=swap';

export function useCaveat() {
  useEffect(() => {
    if (document.querySelector(`link[href="${CAVEAT_HREF}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = CAVEAT_HREF;
    document.head.appendChild(link);
  }, []);
}

/** A red-pen stroke that draws itself. `len` is roughly the path length. */
function Pen({ d, len, delay = 0, className, viewBox, style }) {
  return (
    <svg viewBox={viewBox} className={className} style={{ overflow: 'visible', ...style }} aria-hidden="true">
      <path
        d={d}
        className="redpen-stroke"
        style={{ strokeDasharray: len, strokeDashoffset: len, animationDelay: `${delay}s` }}
      />
    </svg>
  );
}

function Card({ tilt, delay, accent, ruled, paper, inView, settled, to, children }) {
  const Tag = to ? Link : 'article';
  return (
    <Tag
      {...(to ? { to } : {})}
      className="redpen-card group flex flex-col gap-3.5 min-h-[330px] px-6 pt-6 pb-5 rounded-md no-underline text-stone-900 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-stone-900"
      style={{
        '--tilt': tilt,
        transitionDelay: inView && !settled ? `${delay}s` : '0s',
        opacity: inView ? 1 : 0,
        transform: inView ? 'rotate(var(--tilt))' : 'translateY(-24px) rotate(var(--tilt))',
        background: paper,
        backgroundImage: `repeating-linear-gradient(transparent 0 33px, ${ruled} 33px 34px)`,
        backgroundPosition: '0 70px',
        borderTop: `6px solid ${accent}`,
        boxShadow: '0 18px 40px -26px rgba(28,25,23,.45)',
      }}
    >
      {children}
    </Tag>
  );
}

export default function RedPenCards({ exam, data, grammarHref, redemittelHref }) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  // The drop-in is staggered; once it has landed, hover should respond at once.
  const [settled, setSettled] = useState(false);
  useCaveat();

  useEffect(() => {
    if (!inView) return undefined;
    const id = setTimeout(() => setSettled(true), 1200);
    return () => clearTimeout(id);
  }, [inView]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold: 0.35 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const { grammar, vocab, redemittel } = data;
  const note = 'redpen-note text-[26px] leading-none text-red-600';

  return (
    <section ref={ref} className="bg-stone-100 text-stone-900 border-y border-stone-200" aria-labelledby="redpen-heading">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 lg:gap-8">
          <div className="flex flex-col gap-3">
            <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-stone-500">
              {t('mockExam.redPen.eyebrow', { defaultValue: 'Corrected like a teacher would' })}
            </p>
            <h2 id="redpen-heading" className="text-[2rem] sm:text-[2.5rem] leading-[1.05] font-bold tracking-tight" style={DISPLAY}>
              {t('mockExam.redPen.heading', { defaultValue: 'The details examiners mark you down for.' })}
            </h2>
          </div>
          <p className="max-w-sm text-[15px] leading-relaxed text-stone-500">
            {t('mockExam.redPen.intro', {
              pillars: grammar.pillars,
              words: vocab.count,
              phrases: redemittel.count,
              seoName: exam.seoName,
              defaultValue: '{{pillars}} grammar pillars, {{words}} words and {{phrases}} Redemittel for {{seoName}}, written by German teachers.',
            })}
          </p>
        </div>

        <div className="mt-10 sm:mt-12 grid gap-8 lg:gap-10 md:grid-cols-3 items-start">
          <Card tilt="-2.5deg" delay={0.05} accent="#1C1917" ruled="#DBEAFE" paper="#fff" inView={inView} settled={settled} to={grammarHref}>
            <span className="text-[12px] font-semibold uppercase tracking-[0.1em] text-stone-500">{grammar.label}</span>
            <p lang="de" className="mt-6 text-[25px] leading-[34px] font-bold" style={DISPLAY}>
              {grammar.before}{' '}
              <span className="relative inline-block w-7 align-baseline">
                {inView && (
                  <>
                    <Pen viewBox="0 0 20 16" d="M2 14 L10 3 L18 14" len={30} delay={0.5} className="absolute left-1/2 top-[20px] -ml-2.5 w-5 h-4" />
                    {/* Centred by its wrapper: the ink animation owns the note's transform. */}
                    <span className="absolute left-1/2 -top-[26px] -translate-x-1/2">
                      <span className={note} style={{ ...HAND, animationDelay: '.8s' }}>{grammar.insert}</span>
                    </span>
                  </>
                )}
              </span>{' '}
              {grammar.after}
            </p>
            {inView && <span className={note} style={{ ...HAND, animationDelay: '1.1s' }} aria-hidden="true">{grammar.note}</span>}
            <span className="mt-auto text-[14px] leading-normal text-stone-600">{t(`mockExam.redPen.${data.level}.explain`, { defaultValue: grammar.explain })}</span>
            {grammarHref && (
              <span className="inline-flex items-center gap-1.5 text-[14px] font-semibold">
                {t('mockExam.redPen.grammarCta', { level: exam.level, defaultValue: 'Read the {{level}} grammar' })}
                <ArrowRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
              </span>
            )}
          </Card>

          <Card tilt="1.8deg" delay={0.2} accent="#d97706" ruled="#FDE68A" paper="#FFFBEB" inView={inView} settled={settled}>
            <span className="text-[12px] font-semibold uppercase tracking-[0.1em] text-stone-500">{vocab.label}</span>
            <p lang="de" className="mt-6 text-[29px] leading-[34px] font-bold" style={DISPLAY}>
              <span className="relative inline-block mr-2">
                {vocab.article}
                {inView && (
                  <Pen viewBox="0 0 64 44" d="M30 4 C 8 3, 2 18, 6 30 C 12 42, 50 42, 58 26 C 63 12, 46 2, 24 6" len={190} delay={0.3} className="absolute left-[-12px] top-[-8px] w-16 h-11" />
                )}
              </span>{' '}
              {vocab.word}
            </p>
            {inView && <span className={note} style={{ ...HAND, animationDelay: '1s' }} aria-hidden="true">{vocab.note}</span>}
            <span className="text-[15px] text-stone-600">{t(`mockExam.redPen.${data.level}.meaning`, { defaultValue: vocab.meaning })}</span>
            <span lang="de" className="mt-auto text-[14px] leading-normal text-stone-600">{vocab.example}</span>
            <span className="text-[13px] font-semibold text-stone-500">
              {t('mockExam.redPen.vocabCount', { words: vocab.count, topics: vocab.topics, defaultValue: '{{words}} words in {{topics}} topics' })}
            </span>
          </Card>

          <Card tilt="-1.2deg" delay={0.35} accent="#006D77" ruled="#DBEAFE" paper="#fff" inView={inView} settled={settled} to={redemittelHref}>
            <span className="text-[12px] font-semibold uppercase tracking-[0.1em] text-stone-500">{redemittel.label}</span>
            <p lang="de" className="mt-6 text-[25px] leading-[34px] font-bold" style={DISPLAY}>
              <span className="relative inline-block">
                {redemittel.phrase}
                {inView && (
                  <Pen viewBox="0 0 300 14" d="M 4 9 C 60 4, 120 12, 180 6 C 230 3, 270 10, 296 7" len={310} delay={0.4} className="absolute left-0 bottom-[-4px] w-full h-3" style={{ height: 12 }} />
                )}
              </span>
            </p>
            {inView && (
              <span className={`${note} inline-flex items-center gap-1.5`} style={{ ...HAND, animationDelay: '1s' }} aria-hidden="true">
                <Pen viewBox="0 0 26 22" d="M2 12 L 9 19 L 24 2" len={40} delay={1} className="w-[26px] h-[22px]" />
                {redemittel.note}
              </span>
            )}
            <span className="text-[15px] text-stone-600">{t(`mockExam.redPen.${exam.slug}.redemittelMeaning`, { defaultValue: redemittel.meaning })}</span>
            <span className="mt-auto text-[14px] leading-normal text-stone-600">
              {t('mockExam.redPen.redemittelCount', {
                phrases: redemittel.count,
                seoName: exam.seoName,
                defaultValue: '{{phrases}} phrases for {{seoName}} writing and speaking, with a PDF to print.',
              })}
            </span>
            {redemittelHref && (
              <span className="inline-flex items-center gap-1.5 text-[14px] font-semibold">
                {t('mockExam.redPen.redemittelCta', { defaultValue: 'See the phrase list' })}
                <ArrowRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
              </span>
            )}
          </Card>
        </div>
      </div>
    </section>
  );
}
